// Pure data-derivation helpers for the Concept Intelligence drawer.
import type {
  ConceptClassifyResult,
  ConceptFinding,
} from "../../api/contentRegistryApi";
import {
  CONCEPT_DEFINED_SOURCE_HINTS,
  CONCEPT_FLAG_LABELS,
  CONCEPT_RECURRING_SOURCE_HINTS,
  CONCEPT_SOURCE_LABELS,
  CONCEPT_STATUS_PILL,
  CONCEPT_STATUS_UNKNOWN_PILL,
  CONCEPT_CONF_TONES,
  CONFIDENCE_MODERATE_THRESHOLD,
  CONFIDENCE_STRONG_THRESHOLD,
  EMPTY_PLACEHOLDER,
  type StatusPillMeta,
} from "../../constants";
import { pctValue } from "../../utils";

export function isDefinedSource(src: string | undefined | null): boolean {
  const s = String(src ?? "").toLowerCase();
  return CONCEPT_DEFINED_SOURCE_HINTS.some((h) => s.includes(h));
}

export function isRecurringSource(src: string | undefined | null): boolean {
  const s = String(src ?? "").toLowerCase();
  return CONCEPT_RECURRING_SOURCE_HINTS.some((h) => s.includes(h));
}

/* Defined terms carry a section/description; recurring entries are name+count. */
export function findingLooksDefined(f: ConceptFinding): boolean {
  return !!(f.section || (f.description && f.description.trim()));
}

export function humaniseSource(src: string): string {
  const s = String(src || "").toLowerCase();
  if (CONCEPT_SOURCE_LABELS[s]) return CONCEPT_SOURCE_LABELS[s];
  const cleaned = s.replace(/_/g, " ").trim();
  if (!cleaned) return src || EMPTY_PLACEHOLDER;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function humaniseFlag(v: string | undefined | null): string {
  const s = String(v || "").trim().toLowerCase();
  if (!s) return EMPTY_PLACEHOLDER;
  if (CONCEPT_FLAG_LABELS[s]) return CONCEPT_FLAG_LABELS[s];
  const cleaned = s.replace(/_/g, " ");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

export function confidenceTone(v: number | null) {
  if (v === null) return CONCEPT_CONF_TONES.none;
  if (v >= CONFIDENCE_STRONG_THRESHOLD) return CONCEPT_CONF_TONES.strong;
  if (v >= CONFIDENCE_MODERATE_THRESHOLD) return CONCEPT_CONF_TONES.moderate;
  return CONCEPT_CONF_TONES.weak;
}

export function statusPill(status: string | undefined): StatusPillMeta {
  const s = String(status || "").toLowerCase();
  return CONCEPT_STATUS_PILL[s] ?? {
    ...CONCEPT_STATUS_UNKNOWN_PILL,
    label: s ? s : CONCEPT_STATUS_UNKNOWN_PILL.label,
  };
}

/* Flatten concept tree so both response shapes (findings[] vs tree[].concepts[]) work. */
export function flattenFindings(
  result: ConceptClassifyResult | null,
): ConceptFinding[] {
  const tree = result?.metrics?.tree ?? [];
  const treeFindings = tree.flatMap((node) => {
    const groupName = node?.group ?? null;
    const groupDesc = node?.group_description ?? "";
    return (node?.concepts ?? []).map((c) => ({
      ...c,
      group: c.group ?? groupName,
      group_description: c.group_description ?? groupDesc,
      normalized_name: c.normalized_name ?? "",
      parent_concept_id: c.parent_concept_id ?? null,
    }));
  });
  if (treeFindings.length > 0) return treeFindings;
  return result?.findings ?? [];
}

/* Partition findings into "defined" vs "recurring"; fall back on shape. */
export function partitionFindings(findings: ConceptFinding[]): {
  defined: ConceptFinding[];
  recurring: ConceptFinding[];
} {
  return findings.reduce<{ defined: ConceptFinding[]; recurring: ConceptFinding[] }>(
    (acc, f) => {
      if (isRecurringSource(f.source)) {
        acc.recurring.push(f);
      } else if (isDefinedSource(f.source)) {
        acc.defined.push(f);
      } else if (findingLooksDefined(f)) {
        acc.defined.push(f);
      } else {
        acc.recurring.push(f);
      }
      return acc;
    },
    { defined: [], recurring: [] },
  );
}

export function averageConfidence(findings: ConceptFinding[]): number | null {
  const { sum, n } = findings.reduce(
    (acc, f) => {
      const c = pctValue(f.confidence);
      if (c !== null) {
        acc.sum += c;
        acc.n += 1;
      }
      return acc;
    },
    { sum: 0, n: 0 },
  );
  return n > 0 ? sum / n : null;
}

export interface BySourceEntry {
  key: string;
  label: string;
  count: number;
  pct: number;
}

/* Prefer backend's by-source map; otherwise derive from flattened findings. */
export function buildBySourceEntries(
  result: ConceptClassifyResult | null,
  findings: ConceptFinding[],
): BySourceEntry[] {
  const raw = result?.metrics?.by_source;
  const map: Record<string, number> =
    raw && Object.keys(raw).length > 0
      ? (raw as Record<string, number>)
      : findings.reduce<Record<string, number>>((acc, f) => {
        const key = String(f.source ?? "").toLowerCase() || "unknown";
        acc[key] = (acc[key] ?? 0) + 1;
        return acc;
      }, {});
  const totalForPct = Math.max(
    1,
    Object.values(map).reduce((acc, n) => acc + (n ?? 0), 0),
  );
  return Object.entries(map)
    .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
    .map(([src, count]) => ({
      key: src,
      label: humaniseSource(src),
      count: count ?? 0,
      pct: ((count ?? 0) / totalForPct) * 100,
    }));
}

/* Unique sorted list of section anchors present in defined terms. */
export function collectDefinedSections(findings: ConceptFinding[]): string[] {
  const set = new Set<string>(
    findings
      .filter((f) => Boolean(f.section))
      .map((f) => String(f.section)),
  );
  return Array.from(set).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
}

export function maxOccurrences(findings: ConceptFinding[]): number {
  const m = findings.reduce((acc, f) => Math.max(acc, f.occurrences ?? 0), 0);
  return Math.max(1, m);
}

export function sortRecurringFindings(
  findings: ConceptFinding[],
): ConceptFinding[] {
  return [...findings].sort(
    (a, b) => (b.occurrences ?? 0) - (a.occurrences ?? 0),
  );
}

/* Collapse the metrics-vs-partition count fallback chain into one call so
   the drawer body stays declarative. Prefer partitioned counts, then backend
   metrics, then the derived by-source totals. */
export interface ConceptCountsInput {
  metrics: ConceptClassifyResult["metrics"] | undefined;
  findings: ConceptFinding[];
  definedFindings: ConceptFinding[];
  recurringFindings: ConceptFinding[];
}

export interface ConceptCounts {
  total: number;
  definedCount: number;
  recurringCount: number;
  groupsCount: number;
  truncated: number;
  findingsReturned: number;
  ungrouped: number;
  definedCoveragePct: number;
}

export function computeConceptCounts(input: ConceptCountsInput): ConceptCounts {
  const { metrics, findings, definedFindings, recurringFindings } = input;
  const total = metrics?.total ?? findings.length;

  const bySourceMap = metrics?.by_source ?? {};
  const bySourceDefined = Object.entries(bySourceMap).reduce(
    (acc, [k, v]) => acc + (isDefinedSource(k) ? v ?? 0 : 0),
    0,
  );
  const bySourceRecurring = Object.entries(bySourceMap).reduce(
    (acc, [k, v]) => acc + (isRecurringSource(k) ? v ?? 0 : 0),
    0,
  );
  const referencedTerms = (
    metrics as { referenced_terms?: number } | undefined
  )?.referenced_terms;

  const definedCount =
    definedFindings.length > 0
      ? definedFindings.length
      : metrics?.defined_terms ?? referencedTerms ?? bySourceDefined ?? 0;
  const recurringCount =
    recurringFindings.length > 0
      ? recurringFindings.length
      : metrics?.recurring_terms ?? referencedTerms ?? bySourceRecurring ?? 0;

  return {
    total,
    definedCount,
    recurringCount,
    groupsCount: metrics?.groups ?? 0,
    truncated: metrics?.findings_truncated ?? 0,
    findingsReturned: metrics?.findings_returned ?? findings.length,
    ungrouped: metrics?.ungrouped ?? 0,
    definedCoveragePct: total > 0 ? Math.round((definedCount / total) * 100) : 0,
  };
}

/* Trailing "· unlinked" tag when grouping is not enabled. */
export function recurringUnlinkedSuffix(
  groupingPass: string | undefined,
  disabledFlag: string,
  suffix: string,
): string {
  return groupingPass && groupingPass.toLowerCase() === disabledFlag
    ? suffix
    : "";
}

/* ────────────────────────────────────────────────────────────────
   Group-centric derivations for the new Concept Groups section.
   ──────────────────────────────────────────────────────────────── */

export const CONCEPT_UNCLASSIFIED_GROUP = "UNCLASSIFIED";

export type ConceptSourceFilter = "all" | "defined" | "referenced";

export interface ConceptGroup {
  name: string;
  description: string;
  concepts: ConceptFinding[];
  definedCount: number;
  referencedCount: number;
  totalOccurrences: number;
  avgConfidence: number | null;
  isUnclassified: boolean;
}

/** Group findings by their `group` field, preserving group description. */
export function groupFindingsByGroup(
  findings: ConceptFinding[],
): ConceptGroup[] {
  const map = new Map<string, ConceptGroup>();
  for (const finding of findings) {
    const key = finding.group?.trim() || CONCEPT_UNCLASSIFIED_GROUP;
    let group = map.get(key);
    if (!group) {
      group = {
        name: key,
        description: finding.group_description?.trim() ?? "",
        concepts: [],
        definedCount: 0,
        referencedCount: 0,
        totalOccurrences: 0,
        avgConfidence: null,
        isUnclassified: key === CONCEPT_UNCLASSIFIED_GROUP,
      };
      map.set(key, group);
    }
    if (!group.description && finding.group_description?.trim()) {
      group.description = finding.group_description.trim();
    }
    group.concepts.push(finding);
    if (isDefinedSource(finding.source)) group.definedCount += 1;
    if (isRecurringSource(finding.source)) group.referencedCount += 1;
    group.totalOccurrences += finding.occurrences ?? 0;
  }
  for (const group of map.values()) {
    group.avgConfidence = averageConfidence(group.concepts);
    group.concepts.sort(sortConceptsInGroup);
  }
  return Array.from(map.values()).sort(sortGroups);
}

/** Concepts inside a group: defined-first, then by descending occurrences. */
function sortConceptsInGroup(a: ConceptFinding, b: ConceptFinding): number {
  const aDefined = isDefinedSource(a.source) ? 0 : 1;
  const bDefined = isDefinedSource(b.source) ? 0 : 1;
  if (aDefined !== bDefined) return aDefined - bDefined;
  const occDiff = (b.occurrences ?? 0) - (a.occurrences ?? 0);
  if (occDiff !== 0) return occDiff;
  return String(a.name ?? "").localeCompare(String(b.name ?? ""));
}

/** UNCLASSIFIED last; everything else by concept count descending. */
function sortGroups(a: ConceptGroup, b: ConceptGroup): number {
  if (a.isUnclassified !== b.isUnclassified) return a.isUnclassified ? 1 : -1;
  return b.concepts.length - a.concepts.length;
}

/** Filter groups + concepts by search query and source filter. */
export function filterConceptGroups(
  groups: ConceptGroup[],
  searchQuery: string,
  sourceFilter: ConceptSourceFilter,
): ConceptGroup[] {
  const q = searchQuery.trim().toLowerCase();
  const bySource = (finding: ConceptFinding) => {
    if (sourceFilter === "defined") return isDefinedSource(finding.source);
    if (sourceFilter === "referenced") return isRecurringSource(finding.source);
    return true;
  };
  const bySearch = (finding: ConceptFinding) => {
    if (!q) return true;
    const hay =
      `${finding.name ?? ""} ${finding.description ?? ""} ${finding.concept_id ?? ""}`.toLowerCase();
    return hay.includes(q);
  };
  return groups
    .map<ConceptGroup>((group) => ({
      ...group,
      concepts: group.concepts.filter((c) => bySource(c) && bySearch(c)),
    }))
    .filter((group) => group.concepts.length > 0);
}

/** Count total concepts across a list of groups. */
export function totalConceptsAcrossGroups(groups: ConceptGroup[]): number {
  return groups.reduce((acc, g) => acc + g.concepts.length, 0);
}
